<script lang="ts">
  import { onMount } from 'svelte'
  import { i18n, showErrorMessage } from '@/plugins/util'
  import Navbar from '@/components/appDriver/Navbar.svelte'
  import ProfileCard from '@/components/appDriver/ProfileCard.svelte'
  import PageHome from '@/components/appDriver/PageHome.svelte'
  import { isLoading, lang } from '@/store'
  import { axiosInstance } from '@/plugins/axios'
  import dayjs from 'dayjs'
  import Swal from 'sweetalert2'
  import { currentPage } from '@/components/appDriver/store'
  import axios from 'axios'
  import type { DriverTopupHistoryItem } from '@/components/appDriver/types'

  let histories: DriverTopupHistoryItem[] = []

  function getDisplayChannel(item: DriverTopupHistoryItem) {
    return item.display_channel || item.payment_channel
  }

  onMount(() => {
    $currentPage = 'topup-history'
    fetchHistory()
  })

  async function fetchHistory() {
    try {
      $isLoading = true
      const { data } = await axiosInstance.get('/webapp/driver/topup/history')
      histories = data.data as DriverTopupHistoryItem[]

      $isLoading = false
    } catch (error) {
      $isLoading = false

      if (axios.isAxiosError(error)) {
        if (error.response) {
          showErrorMessage(error.response.data.message)
        } else if (error.request) {
          showErrorMessage(i18n($lang, ['Cannot connect to server, please try again', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບ ກະລຸນາລອງໃໝ່']))
        } else {
          showErrorMessage(i18n($lang, ['Please try again', 'ກະລຸນາລອງໃໝ່']))
        }
      } else {
        showErrorMessage(i18n($lang, ['Please try again', 'ກະລຸນາລອງໃໝ່']))
      }
    }
  }

  async function confirmPayment(billNumber: string) {
    try {
      $isLoading = true

      await axiosInstance.post('/webapp/driver/topup/check-payment', {
        'bill_number': billNumber,
      })

      await fetchHistory()

      const Toast = Swal.mixin({
        toast: true,
        position: 'center',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: false,
        width: '240px',
      })

      Toast.fire({
        icon: 'success',
        title: i18n($lang, ['Payment Success', 'ຈ່າຍສຳເລັດ']),
      })

      $isLoading = false
    } catch (error) {
      $isLoading = false

      if (error.response) {
        showErrorMessage(error.response.data.message)
      } else if (error.request) {
        showErrorMessage(i18n($lang, ['Cannot connect to server, please try again', 'ບໍ່ສາມາດເຊື່ອມຕໍ່ລະບົບ ກະລຸນາລອງໃໝ່']))
      } else {
        console.error(`DRIVER_CONFIRM_PAYMENT_ERROR: ${error.message}`)
        showErrorMessage(i18n($lang, ['Please try again', 'ກະລຸນາລອງໃໝ່']))
      }
    }
  }
</script>

<div class="page flex flex-col h-full overflow-hidden bg-[#EFEFEF]">

  <Navbar showBack={true} on:back={() => PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageHome })}/>

  <img src="{new URL('@/assets/general/loca-logo-horizontal.png', import.meta.url).href}" width="100" class="-mt-10 mx-auto">

  <div class="m-4">
    <ProfileCard/>
  </div>

  <div class="text-xl text-center font-semibold m-4">ປະຫວັດການຊຳລະ</div>

  <section class="flex-1 min-h-0 overflow-y-auto bg-white divide-y divide-gray-100 my-4">
    {#if histories.length > 0}
      {#each histories as item}
        <div class="p-4 flex items-center justify-between space-x-4">
          <div class="flex-1 space-y-0.5">
            <div class="{item.payment_status === 'paid' ? 'text-green-600 font-bold' : ''} {item.payment_status === 'pending_payment' ? 'text-red-600 font-light' : ''}">ຈຳນວນ: LAK {item.amount.toLocaleString()}</div>
            <div class="text-xs font-light">ວັນທີ: {dayjs(item.created_at).format('DD/MM/YYYY HH:mm:ss')}</div>
            <div class="text-xs font-light">ເລກອ້າງອີງ: {item.xref}</div>
            {#if item.ref_no}
              <div class="text-xs font-light">ເລກບິນ: {item.ref_no}</div>
            {:else}
              <div class="text-red-600 font-bold">ຍັງບໍ່ທັນຊຳລະ</div>
            {/if}
          </div>

          {#if item.payment_status === 'pending_payment'}
            <div class="rounded bg-[#1D3549] text-white font-semibold py-1 px-2" on:click={() => confirmPayment(item.xref)}>ກວດສອບ</div>
          {/if}

          {#if getDisplayChannel(item) === 'unionpay'}
            <img src="{new URL('@/assets/general/payment-unionpay.png', import.meta.url).href}" width="50" height="50" class="mx-auto object-contain" alt="UnionPay">
          {:else if getDisplayChannel(item) === 'onepay'}
            <img src="{new URL('@/assets/general/onepay-logo.png', import.meta.url).href}" width="50" height="50" class="mx-auto">
          {:else if getDisplayChannel(item) === 'gln'}
            <img src="{new URL('@/assets/general/gln-logo.png', import.meta.url).href}" width="50" height="50" class="mx-auto">
          {:else if getDisplayChannel(item) === 'jdb'}
            <img src="{new URL('@/assets/general/jdb-yes-logo.png', import.meta.url).href}" width="50" height="50" class="mx-auto rounded-lg">
          {:else if getDisplayChannel(item) === 'locapay'}
            <img src="{new URL('@/assets/general/LAOQR-logo.png', import.meta.url).href}" width="50" height="50" class="mx-auto">
          {:else if getDisplayChannel(item) === 'alipay'}
            <img src="{new URL('@/assets/general/alipay-logo.svg', import.meta.url).href}" width="50" height="50" class="mx-auto">
          {:else if getDisplayChannel(item) === 'wechat'}
            <img src="{new URL('@/assets/general/wechat-pay-logo.svg', import.meta.url).href}" width="50" height="50" class="mx-auto" alt="WeChat Pay">
          {/if}

        </div>
      {/each}
    {:else}
      <div class="text-center text-2xl text-gray-300 my-12">
        {i18n($lang, ['No redeem', 'ບໍ່ມີລາຍການ'])}
      </div>
    {/if}
  </section>

</div>
